import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import { Breadcrumb, Button, Form, Input, Row, Select, Space, Spin, Tag, Typography } from 'antd';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { UseCompaniesByQuery, useCompaniesByQuery } from 'src/features/companies/companies.hooks';
import { createCompany, fetchCompanies } from 'src/features/companies/companies.state';
import { GlobalOutlined, ShopTwoTone } from '@ant-design/icons';
import { fetchLocations } from 'src/features/locations/locations.state';
import { useLocationsValues } from 'src/features/locations/locations.hooks';
import { AppDispatch } from 'src/index';
import { CompanyQuery } from 'src/features/companies/companies.types';
import { useDebounce } from 'react-use';

import styles from 'src/components/pages/Company/CompaniesPage/CompaniesPage.module.css';
import tileStyles from 'src/styles/tile.module.css';
import layoutStyles from 'src/styles/layout.module.css';
import { FormattedMessage } from 'react-intl';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';
import { RoleEnum } from 'src/features/users/users.types';

const { Option } = Select;
const { useForm } = Form;

const emptyCompany = { name: '', location: '' };

const NewCompaniesPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const [name, setName] = useState<string>('');
  const [query, setQuery] = useState<Parameters<UseCompaniesByQuery>[0]>({
    name: 'all',
  });

  const companiesByQuery = useCompaniesByQuery(query) as CompanyQuery;
  const locations = useLocationsValues();
  const history = useHistory();

  const [form] = useForm();

  const [createMode, setCreateMode] = useState<boolean>(false);
  const [newEntity, setNewEntity] = useState<{
    name: string;
    location: string;
  }>(emptyCompany);

  const handleCompaniesSearch = useCallback(async () => {
    if (name) {
      dispatch(fetchCompanies({ name }));
      setQuery(q => ({ ...q, name }));
    } else {
      setQuery(q => ({ ...q, name: 'all' }));
    }
  }, [dispatch, name]);

  const handleCreateButtonClick = useCallback(() => {
    setCreateMode(true);
  }, []);

  const handleFormSubmit = useCallback(async () => {
    const response = await dispatch(createCompany(newEntity));

    if (createCompany.fulfilled.match(response)) {
      history.push(`/commercial/companies/${response.payload.id}/devices`);
    }
  }, [dispatch, history, newEntity]);

  useEffect(() => {
    dispatch(fetchLocations({}));
  }, [dispatch]);

  useDebounce(handleCompaniesSearch, 400, [name]);

  return (
    <MainLayout>
      <div className={layoutStyles.mainGrid}>
        <div className={layoutStyles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/commercial/companies'>
                <Space>
                  <ShopTwoTone />
                  Компании
                </Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={layoutStyles.contentArea}>
          <Typography.Title level={2} className={layoutStyles.title}>
            Компании
          </Typography.Title>
          <div className={styles.searchWrap}>
            <Input
              type='text'
              value={name}
              placeholder='Введите название компании'
              style={{ marginRight: 8 }}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div style={{ marginTop: '24px' }}>
            {createMode && (
              <Form
                form={form}
                validateTrigger='onBlur'
                layout='vertical'
                className={tileStyles.listTile}
                onFinish={handleFormSubmit}
              >
                <ShopTwoTone />
                <Form.Item name='name' noStyle>
                  <Input
                    type='text'
                    name='name'
                    placeholder='Введите название компании'
                    style={{ width: '35%' }}
                    onChange={ev => setNewEntity(e => ({ ...e, name: ev.target.value }))}
                  />
                </Form.Item>
                <Form.Item name='location' noStyle>
                  <Select<string>
                    placeholder='Выберите локацию компании'
                    style={{ width: '35%' }}
                    onSelect={ev => setNewEntity(e => ({ ...e, location: ev }))}
                  >
                    {locations.map(l => (
                      <Option key={l.code} value={l.code}>
                        {l.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Button htmlType='button' type='default' onClick={() => setCreateMode(false)}>
                  Отменить
                </Button>
                <Button htmlType='submit' type='primary' disabled={newEntity.name === '' || newEntity.location === ''}>
                  Создать
                </Button>
              </Form>
            )}
            {!companiesByQuery.isPending && companiesByQuery.items.length === 0 && (
              <Row justify='center' style={{ width: '100%', margin: '16px 0' }}>
                <Typography.Text>
                  <FormattedMessage id={'text.NothingFound'} />
                </Typography.Text>
              </Row>
            )}
            <div>
              {companiesByQuery.items.map(c =>
                typeof c !== 'string' ? (
                  <div key={c.id} className={tileStyles.listTile}>
                    <Link to={`/commercial/companies/${c.id}`} className={tileStyles.tileName}>
                      <Space>
                        <ShopTwoTone />
                        {c.name}
                      </Space>
                    </Link>
                    <Space align='center'>
                      {!c.isActive && (
                        <Tag color='red'>
                          <Space>Не активирована</Space>
                        </Tag>
                      )}
                      {c.isTest && <Tag color='orange'>Тестовая</Tag>}
                      {c.disabled && <Tag color='red'>Отключена</Tag>}
                      <Space align='center'>
                        <GlobalOutlined />
                        {c.location.name}
                      </Space>
                    </Space>
                  </div>
                ) : null
              )}
            </div>
            {companiesByQuery.isPending && (
              <Row justify='center' style={{ width: '100%', margin: '16px 0' }}>
                <Spin size='large' spinning />
              </Row>
            )}
          </div>
        </div>
        <div className={layoutStyles.sidebar}>
          <ComponentRoleProtected roles={[RoleEnum.admin]}>
            <Space>
              <Button type='primary' onClick={handleCreateButtonClick}>
                Создать новую компанию
              </Button>
            </Space>
          </ComponentRoleProtected>
        </div>
      </div>
    </MainLayout>
  );
};

const CompaniesPageBlock: React.FC = () => {
  const dispatch = useDispatch();
  const companiesByQuery = useCompaniesByQuery({ name: 'all' });

  useEffect(() => {
    dispatch(fetchCompanies({}));
  }, [dispatch]);

  if (!companiesByQuery || companiesByQuery?.isPending) {
    return (
      <MainLayout>
        <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
          <Spin size={'large'} />
        </Row>
      </MainLayout>
    );
  }

  return <NewCompaniesPage />;
};

export default CompaniesPageBlock;
